
/**
 * @module Calling
 * @description
 */

var Model = require("../types/Model.js");
var xtend = require("xtend");
// eslint-disable-next-line node/no-missing-require
var Buzz = require("buzz");

var RECALCULATE_DELAY = 100;

module.exports = Model({
	name: "calling",
	init: init,
	uninit: uninit,
	listeners: listeners,
	triggers: triggers,

	updateWho: updateWho,

	_who: null,
	_who_conference: null,
	_available: null,
	_people: null,
	_own_available: null,
	_redirecting: false,
	_buzz: null,
	_toast_conference_deny: true,	// Check conference people is deny or hangup for toast.
	_initialized: false
});

/**
 * Initializes the Calling model:
 * set listeners Buzz states
 */
function init() {
	this._buzz = Buzz.createBuzz(this.api, this.dispatcher, this.config);
	this._people = {};
	this._available = {};

	this.push({
		who: {}, // Person we're calling or in a call with
		conference: {},
		streams: {},
		conference_state: "closed",
		state: "idle",
		switching: false
	});
}


function uninit() {
	this._buzz.destroy();
	// clear variables
	this._who = null;
	this._who_conference = null;
	this._available = null;
	this._own_available = null;
	this._people = null;
	this._initialized = false;
}

function listeners() {
	return {
		"buzz:statechange": handle_buzz_state,
		"buzz:conference:statechange": handle_conference_state,
		"buzz:state:idle": handle_idle,

		"buzz:call:incoming": handle_call_in, //Rx incoming call
		"buzz:call:outgoing": handle_call_out, //Rx outgoing call
		"buzz:close": handle_close,

		"buzz:stream": handle_streams, // Streams from call
		"buzz:conference:stream": handle_conference_streams, // Streams from conference call

		"buzz:conference": handle_conference,
		"buzz:redirect": handle_redirect,
		"buzz:silent:notify": handle_silent_notify,
		"buzz:renegotiating": handle_renegotiating,
		"buzz:deny": handle_close,
		"buzz:hangup": handle_hangup,

		"availability:updated": handle_availability_update,

		"personal:updated": handle_personal_update,

		"people:updated": handle_people_update,

		"size:updated": handle_size_update,
	};
}

function triggers() {
	return ["call", "deny", "accept", "hangup", "busy", "idle", "silent"].reduce(function (events, event) {
		events[event] = function (data) {
			this.emit("buzz:trigger:" + event, data);

			event === "call" && this.emit("usermetrics:trigger:track", "initiate call in old calling", {
				who: this._who
			});
			event === "silent" && this.emit("usermetrics:trigger:track", "attempt to initiate call in old calling but contact is busy or offline", {
				caller: this._who
			});
			event === "hangup" && this.emit("usermetrics:trigger:track", "hang up call in old calling", {
				caller: this._who
			});
			event === "deny" && this.emit("usermetrics:trigger:track", "reject call in old calling", {
				caller: this._who
			});
			event === "busy" && this.emit("usermetrics:trigger:track", "contact is busy or offline in old calling", {
				caller: this._who
			});
			event === "accept" && this.emit("usermetrics:trigger:track", "accept call in old calling", {
				caller: this._who
			});
		};
		return events;
	}, {});
}

/**
 * Called whenever the current call is closed
 * Clears the streams, to reset the src tags of video elements
 */
function handle_close() {
	this._who_conference = null;
	this.extend({
		streams: {},
		conference: {}
	});
	this.emit("conferenceinviter:trigger:close");
	this.emit("callmagnifier:trigger:init");
}

function handle_hangup() {
	this.emit("usermetrics:trigger:track", "missed call in old calling", {
		who: this._who
	});
}



/**
 * Called whenever Buzz changes state
 * @param  {String} state The new buzz state. One of calling, receiving, connecting, connected, idle, offline
 */
function handle_buzz_state(state) {
	this.dispatch("statechange", state);
	this.dispatch("state:" + state, state);
	var update = {
		state: state,
	};
	if(state === "idle" || state === "closed")
		update.conference_state = "closed";

	if(state === "connected" || state === "closed")
		update.switching = false;

	this.extend(update);

	if(state === "idle")
		return this.api.calls.outCall();
	else if (state === "receiving" || state === "calling")
		return this.api.calls.inCall();
}

function handle_conference_state(state) {
	if (state !== "closed") {
		this.emit("conferenceinviter:trigger:close");
	}
	this.extend({
		conference_state: state
	});

	if (state === "connected") {
		this._toast_conference_deny = false;
	}

	// If we"re in a call and the conference state is closed, then emit a trigger to toast to say the other party has denied the call
	// current call state is in this.data.state
	// The state argument is the state of the conference call
	// If the current state is "connected", and the conference state is "closed"
	// use this.emit("toast:trigger:show",{message: something})
	if (this.data.state === "connected" && state === "closed" && this._toast_conference_deny) {
		this.emit("desktoptoast:trigger:conference:busy", {
			who: this.data.conference._id
		});
	}

	if (this.data.state === "connected" && state === "closed") {
		this._toast_conference_deny = true;
	}
}

function handle_idle() {
	var redirecting = this._redirecting;
	if (!redirecting) return;
	this._redirecting = null;
	this.emit("buzz:trigger:call", {
		who: redirecting.to,
		voiceOnly: redirecting.voiceOnly
	});
}

/**
 * Called whenever people's state updates. Fetches out data on other person in the call.
 * @param  {Object} people A map of <id - person data>
 */
function handle_people_update(people) {
	this._people = people;
	this.updateWho();
}

function updateWho() {
	var people = this._people;

	var who = this._who;
	var who_data = people[who];

	var conference = this._who_conference;
	var conference_data = people[conference];

	var update = {};
	var shouldUpdate = false;

	if(who_data && this.data.who !== who_data){
		update.who = who_data;
		shouldUpdate = true;
	}

	if(conference_data && this.data.conference !== conference_data){
		update.conference = conference_data;
		shouldUpdate = true;
	}

	if(shouldUpdate)
		this.extend(update);
}

/**
 * Called when Buzz has incoming calls. Fetches data on caller
 * @param  {Object} data Contain's `from`, the caller's id
 */
function handle_call_in(data) {
	var should_answer = this._own_available;
	var initialized = this._initialized;

	if (!should_answer && initialized)
		return this.emit("buzz:trigger:deny", {
			reason: "unavailable"
		});

	var who = data.from;

	this._who = who;

	this.updateWho();

	this.emit("people:trigger:add", {
		lean: true,
		who: who
	});

	this.dispatch("call:incoming", data);
	this.emit("usermetrics:trigger:track", "receive incoming call in old calling", {
		caller: data.from
	});
	this.extend({
		voiceOnly: data.voiceOnly
	});

}

/**
 * Called when Buzz has an outgoing call. Fetches data on callee
 * @param  {Object} data Contains `who`, the calee's id
 */
function handle_call_out(data) {
	var who = data.who;
	this._who = who;

	var is_online = this._available[who];
	if (!is_online) this.emit("buzz:trigger:busy");

	this.updateWho();

	this.emit("people:trigger:add", {
		lean: true,
		who: who
	});
	this.dispatch("call:outgoing", data);
}

/**
 * In 'silent' mode a 'busy' message is created immediately
 * but no call is created.
 * @param  {Object} data Contains 'who', callee _id
 */
function handle_silent_notify(data) {
	var who = data.who;
	this._who = who;
	this.updateWho();
}

/**
 * Handles streams being sent from Buzz
 * @param  {[type]} streams [description]
 * @return {[type]}         [description]
 */
function handle_streams(streams) {
	if (!streams) return;
	var new_streams = xtend(this.data.streams || {}, streams);
	this.extend({
		streams: new_streams
	});
}

function handle_conference_streams(streams) {
	if (!streams) return;
	var new_streams = xtend(
		this.data.streams || {}, {
			conference: streams.remote
		});
	this.extend({
		streams: new_streams
	});
}

function handle_conference(data) {
	var who = data.who;
	this._who_conference = who;

	this.updateWho();

	this.emit("people:trigger:add", {
		lean: true,
		who: who
	});
}

function handle_redirect(data) {
	this._redirecting = data;
	this.emit("buzz:trigger:hangup", {
		reason: "Redirect"
	});
}

function handle_availability_update(available) {
	this._available = available;
}

function handle_personal_update(personal) {
	if(Object.keys(personal).includes("available")) {
		this._own_available = personal.available;
		this._initialized = true;
	}
}

function handle_size_update() {
	if(this.data.state !== "connected")
		return;
	setTimeout(doRecalculate.bind(this), RECALCULATE_DELAY);
}

function doRecalculate() {
	this.emit("buzz:trigger:video:recalculate");
}

function handle_renegotiating(data){
	var reason = data.reason;
	var switching = this.data.switching;
	if(reason === "video" && !switching)
		this.extend({
			switching: true
		});
}